import EnlaceCta from '@/components/marketing/comun/EnlaceCta';
import IconoTrazo, { TRAZO_CHECK } from '@/components/marketing/comun/IconoTrazo';
import { PLANES_CLINICA, precioPorProfesional, type PlanClinica } from '@/content/planesPrecios';

import { CabeceraPlan, FilaPrecio } from './PlanesIndividuales';
import {
    BANDA_VENTAS,
    EQUIVALENCIA_CLINICA,
    TEXTO_CTA_CLINICA,
    VENTAJAS_EQUIPO,
    textoPorProfesional,
} from './textosSeccionPrecios';

export function BloquePrecioClinica({ plan }: { plan: PlanClinica }) {
    // Se calcula aquí y no se lee de un texto: el importe por profesional no puede desviarse del de la clínica.
    const porProfesional = precioPorProfesional(plan.importeMensual, plan.licencias);
    return (
        <div className="flex flex-col gap-1.5">
            <FilaPrecio precio={plan.precio} />
            <span className="self-start rounded-full bg-tint-accent px-2.5 py-[3px] text-ds-100 font-bold text-primary">
                {textoPorProfesional(porProfesional)}
            </span>
        </div>
    );
}

export function Equivalencia({ plan }: { plan: PlanClinica }) {
    return (
        <p className="m-0 rounded-ds-lg border-2 border-ink bg-surface-suave px-4 py-3.5 text-ds-300 leading-normal">
            {EQUIVALENCIA_CLINICA.inicio}
            <strong>{EQUIVALENCIA_CLINICA.sesiones}</strong>
            {EQUIVALENCIA_CLINICA.union}
            <strong>{EQUIVALENCIA_CLINICA.preguntas(plan.preguntasAsistenteMes)}</strong>
            {EQUIVALENCIA_CLINICA.final}
        </p>
    );
}

export function VentajasEquipo() {
    return (
        <ul className="m-0 flex list-none flex-col gap-2.5 p-0">
            {VENTAJAS_EQUIPO.map((ventaja) => (
                <li key={ventaja} className="flex items-start gap-2.5 text-ds-300 leading-[1.45]">
                    <IconoTrazo trazo={TRAZO_CHECK} tamano={18} grosor={2} className="mt-0.5 shrink-0 text-primary" />
                    <span>{ventaja}</span>
                </li>
            ))}
        </ul>
    );
}

function TarjetaClinica({ plan, idTitulo }: { plan: PlanClinica; idTitulo: string }) {
    return (
        <article
            aria-labelledby={idTitulo}
            className="flex flex-col gap-[18px] rounded-xl border-2 border-ink bg-surface-2 px-[18px] py-[22px] shadow-ds-2 lg:p-8"
        >
            <CabeceraPlan id={idTitulo} nombre={plan.nombre} descripcion={plan.descripcion} />
            <BloquePrecioClinica plan={plan} />
            <Equivalencia plan={plan} />
            <VentajasEquipo />
            <EnlaceCta href={plan.enlaceRegistro} variante="primario" anchoCompleto className="mt-auto">
                {TEXTO_CTA_CLINICA}
            </EnlaceCta>
        </article>
    );
}

function BandaVentas() {
    return (
        <div className="flex flex-col items-stretch justify-between gap-3 rounded-ds-lg border-2 border-ink bg-tint-accent px-5 py-4 lg:flex-row lg:items-center">
            <span className="text-ds-400 leading-normal">
                <strong>{BANDA_VENTAS.destacado}</strong>
                {BANDA_VENTAS.resto}
            </span>
            <a
                href={`mailto:${BANDA_VENTAS.correo}`}
                className="box-border flex min-h-11 shrink-0 items-center justify-center rounded-[10px] border-2 border-ink bg-surface-2 px-4 text-ds-300 font-bold text-ink no-underline"
            >
                {BANDA_VENTAS.correo}
            </a>
        </div>
    );
}

/**
 * Las dos clínicas y la banda de ventas (vista «Para tu clínica»). Sin estado propio, pero es de
 * cliente: lo importa la isla SeccionPrecios (`use client`), así que viaja en su JavaScript.
 */
export default function PlanesClinica({ idBase }: { idBase: string }) {
    return (
        <div className="flex flex-col gap-5 lg:gap-7">
            <div className="grid grid-cols-1 items-stretch gap-5 lg:grid-cols-2 lg:gap-7">
                {PLANES_CLINICA.map((plan) => (
                    <TarjetaClinica key={plan.id} plan={plan} idTitulo={`${idBase}-${plan.id}`} />
                ))}
            </div>
            <BandaVentas />
        </div>
    );
}
